import { useEffect, useMemo, useRef, useState } from "react";
import { OperationVariables } from "@apollo/client";
import { useApolloLazyQuery } from "@command-center-app/hooks/useApollo";

type DebounceOptions = {
  debounceMs?: number;
  minLength?: number;    // default 2
  leading?: boolean;     // fire immediately on first keystroke in a burst
  keepPreviousOnError?: boolean;
};

export function useDebouncedSearch<
  TData,
  TVariables extends OperationVariables,
  TOut = TData
>({
  query,
  serviceName,
  variables,         // (term: string) => TVariables
  transform,         // (data: TData) => TOut
  debounceMs = 400,
  minLength = 2,
  leading = false,
  keepPreviousOnError = true,
}: {
  query: any;
  serviceName: string;
  variables: (term: string) => TVariables;
  transform?: (data: TData) => TOut;
} & DebounceOptions) {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<TOut | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const [fetchQuery, { loading, error }] = useApolloLazyQuery<TData, TVariables>(
    serviceName,
    query
  );

  // Keep latest transform/variables stable inside the effect even if callers
  // forget to memoize them.
  const varsRef = useRef(variables);
  const xformRef = useRef(transform);
  useEffect(() => { varsRef.current = variables; }, [variables]);
  useEffect(() => { xformRef.current = transform; }, [transform]);

  // Debounce timer + request sequencing to avoid stale overwrites
  const timeoutRef = useRef<number | null>(null);
  const seqRef = useRef(0); // monotonically increasing request id
  const lastAppliedSeqRef = useRef(0);

  const cancel = () => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const run = (term: string) => {
    const seq = ++seqRef.current;
    setHasSearched(true);

    fetchQuery({
      variables: varsRef.current(term),
      fetchPolicy: "cache-and-network",
      onCompleted: (data: TData) => {
        // Only apply if this is the latest request
        if (seq >= lastAppliedSeqRef.current) {
          lastAppliedSeqRef.current = seq;
          const out = xformRef.current ? xformRef.current(data) : (data as unknown as TOut);
          setResults(out);
        }
      },
      onError: (err: any) => {
        console.error("Debounced search error:", err);
        if (!keepPreviousOnError) setResults(null);
      },
    });
  };

  useEffect(() => {
    const term = searchTerm.trim();

    if (!term || term.length < minLength) {
      cancel();
      setResults(null);
      setHasSearched(false);
      return;
    }

    if (leading && timeoutRef.current === null) {
      // first keystroke in a burst
      run(term);
    }

    cancel();
    timeoutRef.current = window.setTimeout(() => {
      run(term);
      timeoutRef.current = null;
    }, debounceMs);

    return cancel;
    // fetchQuery is stable from the hook; other fns are via refs
  }, [searchTerm, debounceMs, minLength, leading, keepPreviousOnError, fetchQuery]);

  const onChange = useMemo(
    () => (e: { target: { value: string } } | string) =>
      setSearchTerm(typeof e === "string" ? e : e.target.value),
    []
  );

  return {
    // state
    searchTerm,
    results,
    loading,
    error,
    hasSearched,

    // controls
    setSearchTerm,
    onChange,
    cancel,
    runNow: () => {
      const term = searchTerm.trim();
      if (term && term.length >= minLength) {
        cancel();
        run(term);
      }
    },
  };
}
